import React, { useCallback, useEffect, useMemo, useState, memo } from 'react'
import Search from '../../../Common/DaisyTable/Search/Search'
import DaisyTable from '../../../Common/DaisyTable/DaisyTable'
import { searchUtils } from '../../../../utils/search.utils'
import { createColumnsWithCheckbox } from '../../../../utils/table.utils'
import { flexRender, getCoreRowModel, getFilteredRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table'
import { useCapsuleConfigurator } from '../../../../context/CapsuleConfiguratorContext'
import { useOutletContext } from 'react-router-dom'
import SelectionActions from './SelectionActions'
import { getMixtureCountByCapsule, getMixtureCountByCapsuleWithoutEffluences } from '../../../../utils/capsule_configuration.utils'
import LoadingSpinner from '../../../Common/Loading/LoadingSpinner'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline'
import sortingUtils from '../../../../utils/sorting.utils'

// Fonction de comparaison pour MemoizedTableRow
const rowPropsAreEqual = (prevProps, nextProps) => {


    if (prevProps.selectedConfigurationId !== nextProps.selectedConfigurationId) {
        console.log(`[rowPropsAreEqual] Selected configuration change: ${prevProps.selectedConfigurationId} -> ${nextProps.selectedConfigurationId}`);
        return false;
    }

    // Always re-render when column visibility changes
    // This is crucial - we can't optimize this by memoizing, as it needs to reflect visibility changes
    if (prevProps.visibleColumns !== nextProps.visibleColumns) {
        return false;
    }

    // Générer des identifiants stables pour la comparaison
    const prevId = prevProps.row.original.mixture?.id;
    const nextId = nextProps.row.original.mixture?.id;

    // Vérifier si l'ID de la ligne a changé
    const idChanged = prevId !== nextId;
    if (idChanged) return false; // Si l'ID a changé, les props ont certainement changé

    // Vérifier si l'index a changé - cela peut se produire lors d'un tri
    // Nous ne considérons pas ce changement comme significatif pour le rendu
    // car nous utilisons maintenant des identifiants stables basés sur l'ID de mixture

    // Comparaison simplifiée des données importantes
    const prevOriginal = prevProps.row.original;
    const nextOriginal = nextProps.row.original;

    // Vérifier les changements dans les propriétés clés
    // On vérifie en particulier sensibility et dilution qui sont les props qu'on modifie le plus souvent

    if (prevProps.row.index !== nextProps.row.index) {
        return false;
    }

    if (prevOriginal.sensibility !== nextOriginal.sensibility) {
        console.log(`[rowPropsAreEqual] Sensibility change: ${prevOriginal.sensibility} -> ${nextOriginal.sensibility}`);
        return false;
    }

    // Vérifier dilution en tenant compte qu'elle peut être null
    if (
        (prevOriginal.dilution && !nextOriginal.dilution) ||
        (!prevOriginal.dilution && nextOriginal.dilution) ||
        (prevOriginal.dilution && nextOriginal.dilution &&
            prevOriginal.dilution.id !== nextOriginal.dilution.id)
    ) {
        console.log(`[rowPropsAreEqual] Dilution change`);
        return false;
    }

    // Vérifie si capsule a changé
    if (prevOriginal.capsule !== nextOriginal.capsule) {
        console.log(`[rowPropsAreEqual] Capsule change: ${prevOriginal.capsule.name} -> ${nextOriginal.capsule.name}`);
        return false;
    }

    // Vérifier si l'état de sélection a changé en utilisant l'objet selection
    const rowId = prevProps.row.original.mixture.id;
    const wasSelected = Boolean(prevProps.selection[rowId]);
    const isSelected = Boolean(nextProps.selection[rowId]);

    if (wasSelected !== isSelected) {
        console.log(`[rowPropsAreEqual] Selection change for row ${rowId}: ${wasSelected} -> ${isSelected}`);
        return false;
    }

    if (prevProps.capsulesDropdownOptions.length !== nextProps.capsulesDropdownOptions.length) {
        console.log(`[rowPropsAreEqual] Capsules dropdown options change`);

        // Si des options ont été ajoutées, forcer le re-rendu
        if (prevProps.capsulesDropdownOptions.length < nextProps.capsulesDropdownOptions.length) {
            return false;
        }

        // Si des options ont été supprimées, vérifier si la capsule de la ligne est concernée
        const prevCapsuleIds = prevProps.capsulesDropdownOptions.map(opt => opt.id);
        const nextCapsuleIds = nextProps.capsulesDropdownOptions.map(opt => opt.id);
        const removedCapsuleIds = prevCapsuleIds.filter(id => !nextCapsuleIds.includes(id));

        // Vérifier si la ligne utilise une capsule supprimée
        if (removedCapsuleIds.includes(prevProps.row.original.capsule.id)) {
            console.log(`[rowPropsAreEqual] La capsule de cette ligne a été supprimée`);
            // La gestion de la réaffectation sera effectuée ailleurs, mais on doit re-rendre cette ligne
            return false;
        }

        return false; // Pour garantir la mise à jour des lignes même si la capsule n'est pas directement impactée
    }

    // Si on est arrivé jusqu'ici, les props sont considérées comme égales
    return true;
};

// Créer un composant de ligne mémoïsé
const MemoizedRow = memo(({ selection, row, getVisibleCells, visibleColumns, selectedConfigurationId, isInitialConfiguration, capsulesDropdownOptions }) => {
    return (
        <tr className={``}>
            {getVisibleCells().map((cell, index) => {
                if (cell.column.id === 'select' && index === 0) return (
                    <td onClick={(e) => e.stopPropagation()} key={cell.id} style={{ width: cell.column.getSize() }} className='px-2 py-1'>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                )
                return (
                    <td key={cell.id} style={{ width: cell.column.getSize() }} className='px-2 py-1'>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                )
            })}
        </tr>
    );
}, rowPropsAreEqual);

const CapsuleConfigurationTable = ({ capsulesDropdownOptions, isDivisionMode, isInitialConfiguration, columnsVisibility, setColumnsVisibility, columnsDefs, width }) => {
    const { showList, contentConfiguration: configurations, updateConfigurationContent: setConfigurations, setConfigurationSelection: setSelection, configurationSelection: selection, defaultCapsule, selectedConfigurationId, setCountByCapsule, countByCapsule } = useCapsuleConfigurator()
    const columns = columnsDefs
    const [sorting, setSorting] = useState([{
        id: 'subCategory',
        desc: false
    }])
    const [globalFilter, setGlobalFilter] = useState({ keys: [], value: '' })
    const [searchType, setSearchType] = useState(searchUtils.SEARCH_TYPES.fuzzy)

    // Déplacer le useEffect ici, avant les autres fonctions et conditions
    useEffect(() => {
        if (configurations && configurations.length > 0 && isDivisionMode) {
            const count = getMixtureCountByCapsuleWithoutEffluences(configurations);
            setCountByCapsule(count);
        }
    }, [configurations, isDivisionMode, setCountByCapsule]);

    const handleSorting = useCallback((updaterOrValue) => {
        // Gérer les différents types d'entrée que peut recevoir cette fonction
        if (typeof updaterOrValue === 'function') {
            // Si c'est une fonction updater, l'appliquer au state précédent
            setSorting(prev => updaterOrValue(prev));
        } else if (Array.isArray(updaterOrValue)) {
            // Si c'est déjà un tableau, utiliser directement
            setSorting(updaterOrValue);
        } else if (updaterOrValue && typeof updaterOrValue === 'object') {
            // Si c'est un objet (qui n'est pas un tableau), le transformer en tableau
            setSorting([updaterOrValue]);
        } else {
            // Dans les autres cas, réinitialiser à un tableau vide
            setSorting([]);
        }
    }, []);

    const tableData = useMemo(() => {
        if (!configurations || !Array.isArray(configurations)) return []
        let returnedData = configurations.map(item => ({ ...item }));
        // Tri par défaut par catégorie
        returnedData.sort((a, b) => {
            return sortingUtils.sortCategory(a, b, 'mixture.category', false);
        });


        if (globalFilter.value.length > 0 && globalFilter.keys.length > 0) {
            const searchFn = searchType === searchUtils.SEARCH_TYPES.fuzzy ? searchUtils.fuzzySearch : searchUtils.exactSearch
            returnedData = searchFn(returnedData, globalFilter.value, globalFilter.keys)
        }
        if (sorting.length > 0) {
            const sort = sorting[0]
            const { id, desc } = sort
            const sortFn = columnsDefs.find(column => column.id === id)?.sortingFn
            if (sortFn && typeof sortFn === 'function') {
                returnedData.sort((a, b) => {
                    return sortFn(a, b, id, desc)
                })
            }
        }
        return returnedData
    }, [configurations, globalFilter, sorting, searchType, selectedConfigurationId])

    const table = useReactTable({
        data: tableData || null,
        columns: columns,
        columnResizeMode: 'onChange',
        columnResizeDirection: 'ltr',
        defaultColumn: {
            size: 150,
            minSize: 20,
            maxSize: Number.MAX_SAFE_INTEGER,
        },
        state: {
            sorting,
            rowSelection: selection,
            columnVisibility: columnsVisibility

        },
        onSortingChange: handleSorting,
        onRowSelectionChange: setSelection,
        onColumnVisibilityChange: setColumnsVisibility,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        manualSorting: true,
        manualFiltering: true,
        enableRowSelection: true,
        getRowId: (row, index) => {
            return row?.mixture?.id || index
        },
        meta: {
            isInitialConfiguration,
            updateColumn: (rowIndex, columnId, value) => {
                const updatedConfiguration = [...configurations];
                if (columnId.includes('.')) {
                    const [rowId, column] = columnId.split('.');
                    updatedConfiguration[rowIndex] = {
                        ...updatedConfiguration[rowIndex],
                        [rowId]: {
                            ...updatedConfiguration[rowIndex][rowId],
                            [column]: value
                        }
                    };
                } else {
                    updatedConfiguration[rowIndex] = {
                        ...updatedConfiguration[rowIndex],
                        [columnId]: value
                    };
                }
                setConfigurations(updatedConfiguration);
            },
            updateBatchColumns: (rowIndexes, updates) => {
                const updatedConfiguration = [...configurations];
                rowIndexes.forEach(rowIndex => {
                    const row = updatedConfiguration[rowIndex]
                    Object.entries(updates).forEach(([columnId, value]) => {
                        if (columnId.includes('.')) {
                            const [rowId, column] = columnId.split('.')
                            row[rowId][column] = value
                        } else {
                            row[columnId] = value
                        }
                    })
                })
                setConfigurations(updatedConfiguration);
            },
            updateFullRow: (rowIndex, updatedRow) => {
                const updatedData = [...configurations];
                updatedData[rowIndex] = updatedRow;
                setConfigurations(updatedData);
            },
            removeRow: (rowIndex) => {
                if (isInitialConfiguration) return;
                const updatedData = [...configurations];
                const removedRow = updatedData.splice(rowIndex, 1)
                const index = configurations.indexOf(removedRow)
                if (index > -1) {
                    updatedData.splice(index, 1)
                }
                setConfigurations(updatedData);
            },
            removeBatchRows: (rowIndexes) => {
                if (isInitialConfiguration) return;
                const dataCopy = [...configurations];
                const rowToRemove = rowIndexes.map(index => configurations[index])
                rowToRemove.forEach(row => {
                    const index = dataCopy.indexOf(row)
                    dataCopy.splice(index, 1)
                })
                setConfigurations(dataCopy);
            },
            duplicateRows: (rowIndexes) => {
                if (isInitialConfiguration) return;
                const dataCopy = [...configurations];
                const duplicatedRows = rowIndexes.map(index => ({ ...configurations[index] }))
                dataCopy.splice(rowIndexes[0], 0, ...duplicatedRows)
                setConfigurations(dataCopy);
            },
            updateColumnForRows: (rowIndexes, columnId, value) => {
                const updatedConfiguration = [...configurations];
                rowIndexes.forEach(rowIndex => {
                    const row = updatedConfiguration[rowIndex];
                    if (columnId.includes('.')) {
                        const [rowId, column] = columnId.split('.');
                        row[rowId][column] = value;
                    } else {
                        row[columnId] = value;
                    }
                });
                setConfigurations(updatedConfiguration);
            },
            updateCapsuleOnDivide: (mixtureId, value) => {
                const updatedConfiguration = [...configurations];
                updatedConfiguration.forEach(configuration => {
                    if (configuration.mixture.id === mixtureId) {
                        configuration.capsule = value
                    }
                })
                setConfigurations(updatedConfiguration);
                const count = getMixtureCountByCapsuleWithoutEffluences(updatedConfiguration);
                setCountByCapsule(count);
            },
            updateMultipleCapsule: (mixtureIds, value) => {
                const updatedConfiguration = [...configurations];
                mixtureIds.forEach(mixtureId => {
                    updatedConfiguration.forEach(configuration => {
                        if (configuration.mixture.id === mixtureId) {
                            configuration.capsule = value
                        }
                    })
                })
                setConfigurations(updatedConfiguration);
            },
            updateColumnWithMixtureId: (mixtureId, columnId, value) => {
                // Créer une nouvelle copie de la configuration
                const updatedConfiguration = [...configurations];

                // Trouver l'index de l'entrée à mettre à jour en utilisant mixtureId
                const indexToUpdate = updatedConfiguration.findIndex(item => item.mixture.id === mixtureId);

                // Vérifier si l'entrée existe
                if (indexToUpdate === -1) {
                    console.error(`Mixture avec ID ${mixtureId} non trouvée dans la configuration`);
                    return;
                }

                // Mettre à jour la valeur appropriée
                if (columnId.includes('.')) {
                    const [rowId, column] = columnId.split('.');
                    updatedConfiguration[indexToUpdate] = {
                        ...updatedConfiguration[indexToUpdate],
                        [rowId]: {
                            ...updatedConfiguration[indexToUpdate][rowId],
                            [column]: value
                        }
                    };
                } else {
                    updatedConfiguration[indexToUpdate] = {
                        ...updatedConfiguration[indexToUpdate],
                        [columnId]: value
                    };
                }

                // Mettre à jour la configuration
                setConfigurations(updatedConfiguration);
            }
        }
    });

    useEffect(() => {
        if (isDivisionMode) {
            setSorting([{ id: 'sensibility', desc: false }])
        }
    }, [isDivisionMode])

    // Effet pour gérer la suppression d'une capsule et réassigner à la capsule par défaut
    useEffect(() => {
        if (!configurations || !capsulesDropdownOptions || !defaultCapsule) return;

        // Extraire les IDs des capsules disponibles
        const availableCapsuleIds = capsulesDropdownOptions.map(opt => opt.id);

        // Vérifier si des configurations utilisent des capsules qui ne sont plus disponibles
        const updatedConfiguration = [...configurations];
        let hasChanges = false;

        updatedConfiguration.forEach(config => {
            if (config.capsule && !availableCapsuleIds.includes(config.capsule.id)) {
                console.log(`Réassignation de la configuration avec mixture ID ${config.mixture.id} à la capsule par défaut`);
                // Réassigner à la capsule par défaut
                config.capsule = {
                    id: 0,
                    name: defaultCapsule.name,
                    databaseId: defaultCapsule.id
                };
                hasChanges = true;
            }
        });

        // Mettre à jour les configurations si nécessaire
        if (hasChanges) {
            setConfigurations(updatedConfiguration);
            // Mettre à jour le compteur par capsule si en mode division
            if (isDivisionMode) {
                const count = getMixtureCountByCapsuleWithoutEffluences(updatedConfiguration);
                setCountByCapsule(count);
            }
        }
    }, [capsulesDropdownOptions, configurations, defaultCapsule, isDivisionMode, setConfigurations, setCountByCapsule]);

    const headers = table.getFlatHeaders();
    const rows = table.getRowModel().rows;

    if (!configurations) return <LoadingSpinner />

    return (
        <>
            {Object.keys(selection).length > 0 &&
                <SelectionActions
                    capsulesDropdownOptions={capsulesDropdownOptions}
                    isDivisionMode={isDivisionMode}
                    removeBatchRows={table.options.meta.removeBatchRows}
                    updateBatchCapsule={table.options.meta.updateMultipleCapsule}
                    isInitialConfiguration={isInitialConfiguration}
                />
            }
            <div className='flex flex-col gap-4'>

                <div className='flex flex-row justify-between items-center gap-2'>
                    <h3 className='text-lg font-bold'>Selection ({configurations.length})</h3>
                    {!showList && <Search
                        columns={columnsDefs}
                        globalFilter={globalFilter}
                        setGlobalFilter={setGlobalFilter}
                        setSearchType={setSearchType}
                        searchType={searchType}
                        columnsVisibility={columnsVisibility}
                        setColumnsVisibility={setColumnsVisibility}
                    />}
                </div>
                {
                    showList && (
                        <div className='flex flex-row flex-wrap justify-between gap-2'>
                            <p className='text-xs'>Physiques: {configurations.filter(config => config.mixture.category != 'Effluences').length}</p>
                            <p className='text-xs'>Effluences: {configurations.filter(config => config.mixture.category === 'Effluences').length}</p>
                        </div>
                    )
                }


                <div className="overflow-auto">
                    <table style={{ width: width ? width : table.getCenterTotalSize() }} className="table table-sm  m-auto border-spacing-0">
                        <thead>
                            <tr>
                                {headers.map((header) => {
                                    const direction = header.column.getIsSorted();
                                    const arrow = {
                                        asc: <ChevronUpIcon className="w-4 h-4" />,
                                        desc: <ChevronDownIcon className="w-4 h-4" />,
                                    };
                                    const sort_indicator = direction && arrow[direction];

                                    return (
                                        <th key={header.id} colSpan={header.colSpan} style={{ width: header.getSize() }} className='relative px-2 py-1'>
                                            {header.isPlaceholder ? null : (
                                                <div
                                                    onClick={header.column.getToggleSortingHandler()}
                                                    className="cursor-pointer flex gap-4"
                                                    style={{ width: header.getSize() ? header.getSize() : '50px' }}
                                                >
                                                    {flexRender(
                                                        header.column.columnDef.header,
                                                        header.getContext()
                                                    )}
                                                    {direction && <span>{sort_indicator}</span>}
                                                </div>
                                            )}
                                            <div onDoubleClick={() => header.column.resetSize()}
                                                onMouseDown={header.getResizeHandler()}
                                                onTouchStart={header.getResizeHandler()}
                                                className={`resizer ${table.options.columnResizeDirection} ${header.column.getIsResizing() ? 'isResizing' : ''}`}
                                            />
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <MemoizedRow
                                    key={row.id}
                                    row={row}
                                    getVisibleCells={row.getVisibleCells}
                                    visibleColumns={columnsVisibility}
                                    selection={selection}
                                    selectedConfigurationId={selectedConfigurationId}
                                    capsulesDropdownOptions={capsulesDropdownOptions}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    )
}

export default CapsuleConfigurationTable
